import { v4 as uuidv4 } from 'uuid';

import { previewBuilder, readFileToBlob } from '@gem/modules/file-upload';
import { logger } from '@gem/modules/logger';
import { getAudioFileDuration } from '@gem/utils';

import { PayloadType } from 'APP/model/message/messageModel.types';
import { AlbumPayload } from 'STORE/Messages/Message/Payload/AlbumPayload/AlbumPayload';
import { AudioPayload } from 'STORE/Messages/Message/Payload/AudioPayload/AudioPayload';
import { FilePayload } from 'STORE/Messages/Message/Payload/FilePayload/FilePayload';
import { ImagePayload } from 'STORE/Messages/Message/Payload/ImagePayload/ImagePayload';
import { VideoPayload } from 'STORE/Messages/Message/Payload/VideoPayload/VideoPayload';

import {
  TMediaMessagePayload,
  TMediaItemPayload,
  TMediaItemPayloadType,
} from './MediaMessagePopup.types';

const MAX_SIZE_TO_BLOB = 100 * 1024 * 1024; //100MB

export function isMediaFile(fileType: string): boolean {
  return (
    ['video', 'image', 'audio'].includes(fileType.split('/')[0]) && fileType !== 'image/svg+xml'
  );
}

export function isMediaPayload(payload: TMediaMessagePayload): boolean {
  return [PayloadType.Video, PayloadType.Image].includes(payload.payloadType);
}

export function isMediaItemPayloadType(
  payloadType?: PayloadType
): payloadType is TMediaItemPayloadType {
  if (!payloadType) {
    return false;
  }

  return [
    PayloadType.Image,
    PayloadType.File,
    PayloadType.AudioMessage,
    PayloadType.Video,
  ].includes(payloadType);
}

async function getAudioPayload(
  file: File | Blob,
  fileName: string
): Promise<AudioPayload | FilePayload> {
  const fileData = {
    comment: '',
    fileName,
    fileSize: file.size,
    mimeType: file.type,
    userFile: file,
  };

  try {
    const duration = await getAudioFileDuration(file);
    return new AudioPayload({ ...fileData, duration });
  } catch (error) {
    logger.get('MediaMessage').warn(`\`unsupported audio file: ${fileName}`, error);
    return new FilePayload(fileData);
  }
}

async function getVideoPayload(
  file: File | Blob,
  fileName: string
): Promise<VideoPayload | FilePayload> {
  const fileData = {
    comment: '',
    fileName,
    fileSize: file.size,
    mimeType: file.type,
    userFile: file,
  };

  try {
    const { thumbnail, width, height, duration } =
      await previewBuilder.getFirstVideoFrameAndDuration(file);

    return new VideoPayload({
      ...fileData,
      graphicSize: { width, height },
      localPreview: thumbnail,
      duration,
    });
  } catch (error) {
    logger.get('MediaMessage').warn(`unsupported video file: ${fileName}`, error);
    return new FilePayload(fileData);
  }
}

async function getImagePayload(
  file: File | Blob,
  fileName: string
): Promise<ImagePayload | FilePayload> {
  const fileData = {
    comment: '',
    fileName,
    fileSize: file.size,
    mimeType: file.type,
    userFile: file,
  };

  return new Promise((resolve) => {
    const img = new Image();

    img.onload = (): void => {
      resolve(
        new ImagePayload({
          ...fileData,
          graphicSize: { width: img.width, height: img.height },
          localPreview: img.src,
        })
      );
    };

    img.onerror = (error): void => {
      logger.get('MediaMessage').warn(`unsupported image file: ${fileName}`, error);
      URL.revokeObjectURL(img.src);
      resolve(new FilePayload(fileData));
    };

    img.src = URL.createObjectURL(file);
  });
}

export async function getFilePayload(
  file: File | Blob,
  fileName: string,
  isSendAsFile?: boolean
): Promise<TMediaItemPayload> {
  const fileType = file.type;
  let _file: File | Blob = file;

  if (file.size <= MAX_SIZE_TO_BLOB && file instanceof File) {
    _file = await readFileToBlob(file);
  }

  if (!isMediaFile(fileType) || isSendAsFile) {
    return new FilePayload({
      comment: '',
      fileName,
      fileSize: file.size,
      mimeType: file.type,
      userFile: file,
    });
  }

  switch (0) {
    case fileType.indexOf('video'):
      return await getVideoPayload(_file, fileName);
    case fileType.indexOf('audio'):
      return await getAudioPayload(_file, fileName);
    default:
      return await getImagePayload(_file, fileName);
  }
}

export function getAlbumPayload(payloads: TMediaItemPayload[]): AlbumPayload {
  return new AlbumPayload({
    comment: '',
    payloads: payloads.map((payload, order) => {
      return {
        payloadType: PayloadType.ContainerOrdered,
        payload: payload.toJSON(),
        id: uuidv4(),
        order,
      };
    }),
  });
}
